"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { WCallContent } from "@/lib/api/cms-window-content";
import {
  Field,
  Input,
  Select,
  FormStatus,
  Honeypot,
  SubmitButton,
} from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { toast } from "@/lib/notifications/toast";
import { useT } from "@/lib/i18n/context";
import { useAntiSpamToken } from "@/lib/cms/useAntiSpamToken";
import { wCallTranslations, type WCallTranslations } from "./WCall.i18n";
import styles from "./WCall.module.css";

/**
 * WCall - okno "oddzwonimy" (`w_call`) - floating widget z formularzem kontaktu.
 *
 * **UX:**
 *   1. Po `config.delay_time` ms od mount'u widget pojawia się w lewym dolnym
 *      rogu (avatar konsultanta + zachęcający tekst + CTA "Zostaw numer").
 *   2. Klik CTA otwiera modal-form: pole telefonu + 2 przyciski - "Teraz"
 *      (natychmiast) lub "Później" (umówiony slot).
 *   3. Klik "Później" przełącza modal w drugi krok: select dnia + godziny + CTA.
 *   4. Po sukcesie toast + zamknięcie modala. Cookie `wcall_dismissed_{id}`
 *      ukrywa widget na 7 dni od zamknięcia/sukcesu.
 *
 * **Anti-spam:** HMAC token + min-delay 3s + honeypot `website` + rate-limit
 * per IP (5/godz) - waliduje backend, frontend forward'uje token+timestamp z
 * `content.antispam`.
 *
 * **Walidacja UI:** RHF + Zod (analogicznie do WContactForm). Telefon - liberalna
 * walidacja regex (>= 7 cyfr, znaki `+`/`-`/spacja dozwolone); dzien/godzina -
 * enum z `content.select_data`.
 */
export function WCall({ content }: WCallProps) {
  const t = useT(wCallTranslations);

  if (!content || typeof content !== "object") {
    return null;
  }

  if (content.config.send_to === "1" && !content.config.crm_available) {
    return (
      <div className={styles.fallback}>
        <FormStatus variant="warning">
          {t.crmModeWithoutModule.body1}
          <strong> {t.crmModeWithoutModule.moduleName}</strong>{" "}
          {t.crmModeWithoutModule.body2}
        </FormStatus>
      </div>
    );
  }

  if (content.config.send_to !== "1" && content.config.send_to !== "2") {
    return (
      <div className={styles.fallback}>
        <FormStatus variant="error">
          {t.unknownSendMode(content.config.send_to)}
        </FormStatus>
      </div>
    );
  }

  return <CallWidget content={content} t={t} />;
}

export type WCallProps = {
  content: WCallContent | null;
};

/* ─── Floating widget + modal ─────────────────────────────────────────── */

type Mode = "closed" | "now" | "later";

const COOKIE_TTL_DAYS = 7;

function CallWidget({
  content,
  t,
}: {
  content: WCallContent;
  t: WCallTranslations;
}) {
  /* Visible = false na SSR + przed delay'em; staje się true po `delay_time`
     ms w efekcie client-only. Cookie `wcall_dismissed_{id}` ukrywa na 7 dni
     po zamknięciu/sukcesie - powtórne wyświetlenie tej samej osobie byłoby
     spamem. */
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<Mode>("closed");
  /* Telefon trzymany na poziomie widgetu - przezywa przelaczenia
     "Teraz" ↔ "Pozniej" (RHF form unmount-uje sie przy zmianie kroku). */
  const [phone, setPhone] = useState("");

  useEffect(() => {
    const cookieName = `wcall_dismissed_${content.window_id}`;
    if (getCookie(cookieName)) return;

    const timer = window.setTimeout(() => {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisible(true);
    }, content.config.delay_time);

    return () => window.clearTimeout(timer);
  }, [content.window_id, content.config.delay_time]);

  const closeAndRemember = () => {
    const cookieName = `wcall_dismissed_${content.window_id}`;
    setCookie(cookieName, "1", COOKIE_TTL_DAYS);
    setVisible(false);
    setMode("closed");
  };

  if (!visible) return null;

  return (
    <>
      <div className={styles.floating} role="complementary" aria-label={content.config.title || t.widgetAriaLabel}>
        <div className={styles.bubble}>
          <button
            type="button"
            className={styles.close}
            aria-label={t.closeLabel}
            onClick={closeAndRemember}
          >
            <Icon name="x" />
          </button>
          {content.config.title && <h3 className={styles.title}>{content.config.title}</h3>}
          {content.config.text && <p className={styles.text}>{content.config.text}</p>}
          <button
            type="button"
            className={styles.ctaPrimary}
            onClick={() => setMode("now")}
          >
            <Icon name="phone" />
            <span>{t.cta.leaveNumber}</span>
          </button>
        </div>
      </div>

      {mode !== "closed" &&
        createPortal(
          <CallModal
            content={content}
            t={t}
            mode={mode}
            phone={phone}
            setPhone={setPhone}
            onBack={() => setMode("now")}
            onPickLater={() => setMode("later")}
            onCancel={() => setMode("closed")}
            onSuccess={closeAndRemember}
          />,
          document.body,
        )}
    </>
  );
}

/* ─── Modal z formularzem ─────────────────────────────────────────────── */

function CallModal({
  content,
  t,
  mode,
  phone,
  setPhone,
  onBack,
  onPickLater,
  onCancel,
  onSuccess,
}: {
  content: WCallContent;
  t: WCallTranslations;
  mode: "now" | "later";
  phone: string;
  setPhone: (value: string) => void;
  onBack: () => void;
  onPickLater: () => void;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className={styles.backdrop} onClick={onCancel}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`wcall-title-${content.window_id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.modalClose}
          aria-label={t.closeLabel}
          onClick={onCancel}
        >
          <Icon name="x" />
        </button>
        {mode === "now" ? (
          <CallNowForm
            content={content}
            t={t}
            phone={phone}
            setPhone={setPhone}
            onSuccess={onSuccess}
            onPickLater={onPickLater}
          />
        ) : (
          <CallLaterForm
            content={content}
            t={t}
            phone={phone}
            setPhone={setPhone}
            onSuccess={onSuccess}
            onBack={onBack}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Formularz "Teraz" (telefon + 2 akcje) ───────────────────────────── */

function CallNowForm({
  content,
  t,
  phone,
  setPhone,
  onSuccess,
  onPickLater,
}: {
  content: WCallContent;
  t: WCallTranslations;
  phone: string;
  setPhone: (value: string) => void;
  onSuccess: () => void;
  onPickLater: () => void;
}) {
  const schema = useMemo(
    () =>
      z.object({
        phone: z
          .string()
          .trim()
          .min(1, t.validation.requiredPhone)
          .refine(isPhoneShape, t.validation.invalidPhone),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { phone },
    mode: "onTouched",
  });

  const [topError, setTopError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  /* Czas mountu - min-delay liczony lokalnie (clock skew immunity). */
  const [mountedAt] = useState(() => Date.now());

  /* Fresh anti-spam token per mount - bypass Next.js SWR cache SSR'a. */
  const antispam = useAntiSpamToken([content.window_id]);

  const onPickLaterClick = async () => {
    /* "Później" też wymaga zwalidowanego telefonu - validacja przed
       przełączeniem kroku (inaczej user idzie dalej z pustym/błędnym
       numerem i submit drugiego kroku zwróci to samo). Numer
       przekazujemy do parenta, aby krok "Później" mógł go prefillować. */
    const valid = await trigger("phone");
    if (!valid) return;
    setPhone(getValues("phone"));
    onPickLater();
  };

  const onSubmit = async (values: FormValues) => {
    setTopError(null);

    const ageSec = Math.floor((Date.now() - mountedAt) / 1000);
    if (ageSec < content.antispam.min_delay_seconds) {
      setTopError(
        t.status.waitBeforeSubmit(content.antispam.min_delay_seconds - ageSec),
      );
      return;
    }

    if (!antispam.token || !antispam.timestamp) {
      setTopError(antispam.error ?? t.status.sendFailed);
      return;
    }

    await sendCall(
      "/api/w-call/save-call",
      {
        id_window: content.window_id,
        token: antispam.token,
        timestamp: antispam.timestamp,
        [content.antispam.honeypot_field]: honeypot,
        phone: values.phone,
        url_from_call: typeof window !== "undefined" ? window.location.href : "",
      },
      t,
      content,
      onSuccess,
      setTopError,
    );
  };

  /* Dwa typy akcji - "Później" (przełącza krok, validuje tylko telefon)
     oraz "Teraz" (full submit + POST). RHF `handleSubmit` jest dla "Teraz". */

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h3 id={`wcall-title-${content.window_id}`} className={styles.formTitle}>
        {t.now.title}
      </h3>
      <p className={styles.formIntro}>{t.now.intro}</p>

      <Field
        label={content.lang.labels?.telephone || t.labels.telephone}
        required
        error={typeof errors.phone?.message === "string" ? errors.phone.message : undefined}
      >
        <Input
          type="tel"
          autoComplete="tel"
          disabled={isSubmitting}
          prefix={<Icon name="phone" />}
          {...register("phone")}
        />
      </Field>

      <Honeypot
        name={content.antispam.honeypot_field}
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />

      {topError && <FormStatus variant="error">{topError}</FormStatus>}

      {content.config.text_rodo && (
        <p className={styles.rodo}>{content.config.text_rodo}</p>
      )}

      <div className={styles.actionsTwo}>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => void onPickLaterClick()}
          disabled={isSubmitting}
        >
          {content.lang.labels?.btn_later || t.now.btnLater}
        </button>
        <SubmitButton
          loading={isSubmitting || !antispam.ready}
          loadingLabel={t.sending}
          disabled={antispam.error != null}
        >
          {content.lang.labels?.btn_now || t.now.btnNow}
        </SubmitButton>
      </div>
    </form>
  );
}

/* ─── Formularz "Pozniej" (telefon + dzien + godzina) ─────────────────── */

function CallLaterForm({
  content,
  t,
  phone,
  setPhone,
  onSuccess,
  onBack,
}: {
  content: WCallContent;
  t: WCallTranslations;
  phone: string;
  setPhone: (value: string) => void;
  onSuccess: () => void;
  onBack: () => void;
}) {
  const allowedDays = useMemo(
    () => content.select_data.days.map((d) => String(d.key)),
    [content.select_data.days],
  );
  const allowedHours = content.select_data.hours;

  const schema = useMemo(
    () =>
      z.object({
        phone: z
          .string()
          .trim()
          .min(1, t.validation.requiredPhone)
          .refine(isPhoneShape, t.validation.invalidPhone),
        day: z
          .string()
          .min(1, t.validation.requiredDay)
          .refine((v) => allowedDays.includes(v), t.validation.invalidDay),
        hour: z
          .string()
          .min(1, t.validation.requiredHour)
          .refine((v) => allowedHours.includes(v), t.validation.invalidHour),
      }),
    [t, allowedDays, allowedHours],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { phone, day: "", hour: "" },
    mode: "onTouched",
  });

  const [topError, setTopError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [mountedAt] = useState(() => Date.now());

  /* Fresh anti-spam token per mount - bypass Next.js SWR cache SSR'a. */
  const antispam = useAntiSpamToken([content.window_id]);

  const onSubmit = async (values: FormValues) => {
    setTopError(null);

    const ageSec = Math.floor((Date.now() - mountedAt) / 1000);
    if (ageSec < content.antispam.min_delay_seconds) {
      setTopError(
        t.status.waitBeforeSubmit(content.antispam.min_delay_seconds - ageSec),
      );
      return;
    }

    if (!antispam.token || !antispam.timestamp) {
      setTopError(antispam.error ?? t.status.sendFailed);
      return;
    }

    await sendCall(
      "/api/w-call/save-call-later",
      {
        id_window: content.window_id,
        token: antispam.token,
        timestamp: antispam.timestamp,
        [content.antispam.honeypot_field]: honeypot,
        phone: values.phone,
        day: Number(values.day),
        hour: values.hour,
        url_from_call: typeof window !== "undefined" ? window.location.href : "",
      },
      t,
      content,
      onSuccess,
      setTopError,
    );
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h3 id={`wcall-title-${content.window_id}`} className={styles.formTitle}>
        {t.later.title}
      </h3>
      <p className={styles.formIntro}>{t.later.intro}</p>

      <Field
        label={content.lang.labels?.telephone || t.labels.telephone}
        required
        error={typeof errors.phone?.message === "string" ? errors.phone.message : undefined}
      >
        <Input
          type="tel"
          autoComplete="tel"
          disabled={isSubmitting}
          prefix={<Icon name="phone" />}
          {...register("phone")}
        />
      </Field>

      <div className={styles.twoCol}>
        <Field
          label={content.lang.labels?.day || t.labels.day}
          required
          error={typeof errors.day?.message === "string" ? errors.day.message : undefined}
        >
          <Select
            placeholder={content.lang.labels?.select_day || t.placeholders.selectDay}
            disabled={isSubmitting}
            {...register("day")}
          >
            {content.select_data.days.map((d) => (
              <option key={d.key} value={String(d.key)}>
                {d.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={content.lang.labels?.hour || t.labels.hour}
          required
          error={typeof errors.hour?.message === "string" ? errors.hour.message : undefined}
        >
          <Select
            placeholder={content.lang.labels?.select_hour || t.placeholders.selectHour}
            disabled={isSubmitting}
            {...register("hour")}
          >
            {content.select_data.hours.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Honeypot
        name={content.antispam.honeypot_field}
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />

      {topError && <FormStatus variant="error">{topError}</FormStatus>}

      {content.config.text_rodo && (
        <p className={styles.rodo}>{content.config.text_rodo}</p>
      )}

      <div className={styles.actionsTwo}>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => {
            /* Wstecz - zachowujemy aktualnie wpisany numer, zeby krok
               "Teraz" prefillowal sie tym samym co user wlasnie wpisal. */
            setPhone(getValues("phone"));
            onBack();
          }}
          disabled={isSubmitting}
        >
          {t.later.btnBack}
        </button>
        <SubmitButton
          loading={isSubmitting || !antispam.ready}
          loadingLabel={t.sending}
          disabled={antispam.error != null}
        >
          {content.lang.labels?.btn_save_later || t.later.btnSubmit}
        </SubmitButton>
      </div>
    </form>
  );
}

/* ─── Wspólny wrapper wysyłki (POST + mapowanie błędów) ───────────────── */

async function sendCall(
  endpoint: string,
  body: Record<string, unknown>,
  t: WCallTranslations,
  content: WCallContent,
  onSuccess: () => void,
  setTopError: (msg: string | null) => void,
): Promise<void> {
  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      message?: string;
      error?: string;
      code?: number;
      errors?: Record<string, string>;
    };

    if (!res.ok || !json.ok) {
      /* Per-pole errors (code 1010) - sklejamy w jeden komunikat na górze
         (modal jest mały, full-pole highlight nie mieści się obok inputu). */
      if (json.errors && Object.keys(json.errors).length > 0) {
        setTopError(Object.values(json.errors).filter((v): v is string => typeof v === "string").join(" "));
      } else {
        setTopError(
          json.message ||
            (res.status === 429 ? t.status.rateLimit : t.status.sendFailed),
        );
      }
      return;
    }

    toast.success(json.message || content.lang.text?.text_success || t.status.sendSuccess);
    onSuccess();
  } catch (err) {
    setTopError(
      err instanceof Error ? t.status.networkError(err.message) : t.status.networkErrorGeneric,
    );
  }
}

/* ─── Helpers ─────────────────────────────────────────────────────────── */

/** Liberalna walidacja telefonu - znaki `+0-9 \-()` + min 7 cyfr (parytet z backend). */
function isPhoneShape(value: string): boolean {
  if (!/^[+0-9\s\-()]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, "");
  return digits.length >= 7;
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const m = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === "undefined") return;
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/`;
}
